/**
 * Downscale a raster image File before upload so a full-size phone photo (7MB+) doesn't
 * trip the server's "file too large" limit. Draws the image onto a canvas capped at
 * ~2048px on the longest edge and re-encodes as JPEG at ~0.85 quality.
 *
 * Non-images (and images already small enough) are returned untouched, so this is safe to
 * call on any File before appending it to a FormData.
 */
const MAX_EDGE = 2048;
const SKIP_UNDER_BYTES = 1.5 * 1024 * 1024;   // already small — don't bother re-encoding
const JPEG_QUALITY = 0.85;

export async function downscaleImage(file: File): Promise<File> {
  // Only touch raster images; leave everything else (and small images) as-is.
  if (!file.type.startsWith('image/')) return file;

  let bitmap: HTMLImageElement;
  try {
    bitmap = await loadImage(file);
  } catch {
    return file;   // couldn't decode — send the original rather than lose the upload
  }

  const longest = Math.max(bitmap.naturalWidth, bitmap.naturalHeight);
  if (file.size < SKIP_UNDER_BYTES && longest <= MAX_EDGE) return file;

  const scale = longest > MAX_EDGE ? MAX_EDGE / longest : 1;
  const w = Math.round(bitmap.naturalWidth * scale);
  const h = Math.round(bitmap.naturalHeight * scale);

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  ctx.drawImage(bitmap, 0, 0, w, h);

  const blob = await new Promise<Blob | null>(resolve =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
  if (!blob || blob.size >= file.size) return file;   // no win — keep the original

  const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
  return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode failed')); };
    img.src = url;
  });
}
